import { useRef, useState } from "react";
import SVG from "react-inlinesvg";
import styled from "styled-components/macro";
import { useDraggable } from "@dnd-kit/core";
import { useStructuredOutlineStore } from "../../StructuredOutlineStore";
import DynamicInputLength from "../../../../DynamicInputLength";
import ThreeDotsSelect from "src/sudokit/components/SudoSelect/ThreeDotsSelect";
import { AppEventPublisher } from "../../../../../AppEventPublisher";
import { themeVariables } from "../../../../../themeVariables";
import AnimatedWrapper from "src/components/AnimatedWrapper";
import { cn } from "src/lib/cn.js";
import { useStableCallback } from "src/hooks/useStableCallback";

const ActContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-left: 30px;
  margin-right: 24px;

  gap: 2px;
  span {
    font-size: 18px;
    font-weight: 500;
  }
  input {
    font-family: "Inter";
    background-color: transparent;
    margin-right: 10px;
    font-size: 18px;
    font-weight: 500;
    border: 1px solid transparent;
    max-width: 100%;
    min-width: 0px;
    width: auto;
    border-radius: 4px;
    padding: 6px 6px;
    color: var(--grey-0);
    &:hover {
      border: 1px solid var(--menu-color-tinted3);
    }
  }

  input:focus {
    border: 1px solid var(--primary-cta);
  }
  svg {
    color: ${themeVariables.grey2};
    min-width: 24px;
  }
  .act-title-diff {
    color: ${themeVariables.primaryCTAHighContrast};
    font-weight: 600;
  }
`;

function Act({ act, draggingItemId, readOnly, syncing }) {
  const menuRef = useRef();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef } = useDraggable({ id: act.id, disabled: readOnly });
  const nameInputRef = useRef();
  const updateItemName = useStructuredOutlineStore((state) => state.updateItemName);
  const deleteItem = useStructuredOutlineStore((state) => state.deleteItem);
  const chapterDiffs = useStructuredOutlineStore((state) => state.chapterDiffs);
  const clearChapterDiff = useStructuredOutlineStore((state) => state.clearChapterDiff);

  const actDiff = chapterDiffs[act.id];
  const hasDiff = actDiff && actDiff.oldContent && actDiff.newContent;
  const hasNameDiff = hasDiff && actDiff.newContent?.name && actDiff.oldContent?.name !== actDiff.newContent.name;
  const handleDelete = () => {
    AppEventPublisher.storyBible.outline.delete("act");
    return deleteItem(act.id);
  };
  const isDragging = draggingItemId === act.id;
  const dragCursor = isDragging ? "grabbing" : "grab";
  const [hoverButtonsVisible, setHoverButtonsVisible] = useState(false);

  const handleRename = () => {
    setTimeout(() => {
      nameInputRef.current.select();
    }, 100);
  };

  const handleMenuChange = useStableCallback((option) => {
    switch (option) {
      case "Rename":
        handleRename();
        break;
      case "Delete":
        handleDelete();
        break;
      default:
        break;
    }
  });

  return (
    <ActContainer
      ref={setNodeRef}
      onPointerEnter={() => {
        setHoverButtonsVisible(true);
      }}
      onPointerLeave={() => {
        setHoverButtonsVisible(false);
      }}
      onFocus={() => {
        setHoverButtonsVisible(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setHoverButtonsVisible(false);
        }
      }}
    >
      <div className="flex items-center w-full">
        {!readOnly && (
          <AnimatedWrapper
            isVisible={hoverButtonsVisible}
            applyToChildren={false}
          >
            <SVG
              ref={setActivatorNodeRef}
              {...listeners}
              {...attributes}
              style={{ cursor: dragCursor }}
              src="/assets/grabber.svg"
              height={24}
              width={24}
            />
          </AnimatedWrapper>
        )}
        <div className="w-full">
          <DynamicInputLength
            ref={nameInputRef}
            value={act.name}
            resizeTrigger={act.name}
            onChange={(e) => {
              if (hasDiff) {
                clearChapterDiff(act.id);
              }
              updateItemName(act.id, e.target.value);
            }}
            onFocus={() => {
              if (hasDiff) {
                clearChapterDiff(act.id);
              }
            }}
            placeholder="Act Name"
            disabled={readOnly || syncing}
            maxLength={100}
            className={cn("transition-opacity", syncing && "opacity-50", hasNameDiff && "act-title-diff")}
          />
        </div>
      </div>
      <div>
        <ThreeDotsSelect
          ref={menuRef}
          options={[
            { value: "Rename", label: "Rename", icon: { src: "/assets/pencil.svg", label: "Rename", status: "default" } },
            { value: "Delete", label: "Delete", icon: { src: "/assets/delete.svg", label: "Delete", status: "danger" } },
          ]}
          onChange={handleMenuChange}
          disabled={readOnly || syncing}
          optionAlignment="left"
          className="[&&]:p-1 mr-[3px]"
          align="end"
        />
      </div>
    </ActContainer>
  );
}

export default Act;
